import dayjs, { Dayjs } from "dayjs";
import { useGetBusinessItemsForMonthQuery } from "lib/features/item/itemService";
import { FareharborAvailability, Item } from "lib/features/item/types";
import { Availability } from "lib/features/availability/types";
import { useLazyGetMyBookingsQuery } from "lib/features/booking/bookingService";
import { useDeleteAvailabilityMutation } from "lib/features/availability/availabilityService";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { getBusinessName } from "./utils/availabilityHelpers";
import {
  CalendarAvailability,
  indexAvailabilitiesByDay,
} from "./utils/calendarUtils";

interface AvailabilitiesProviderProps {
  children: ReactNode;
}

interface SelectedSlot {
  item: Item;
  availability: Availability | FareharborAvailability;
  isFareharbor: boolean;
}

interface SelectedAvailability {
  itemId: number;
  availabilityId: string;
  availability: CalendarAvailability;
}

interface SelectionCounts {
  deletableCount: number;
  hideableCount: number;
}

interface AvailabilitiesContextValue {
  currentMonth: Dayjs;
  setCurrentMonth: (date: Dayjs) => void;
  navigateMonth: (direction: "prev" | "next") => void;
  items: Item[];
  filteredItems: Item[];
  selectedItem: string;
  setSelectedItem: (item: string) => void;
  isLoading: boolean;
  selectedDate: string | null;
  setSelectedDate: (date: string | null) => void;
  getAvailabilitiesForDate: (date: string) => CalendarAvailability[];
  selectedSlot: SelectedSlot | null;
  setSelectedSlot: (slot: SelectedSlot | null) => void;
  selectAvailability: (calendarAvailability: CalendarAvailability) => void;
  getItemById: (id: number, availabilityId: number) => Item | undefined;
  // Booking stats
  getBookingCountForItem: (date: string, itemId: number) => number;
  // Selection
  selectedAvailabilities: SelectedAvailability[];
  selectionCounts: SelectionCounts;
  handleCheckboxChange: (
    availability: CalendarAvailability,
    isSelected: boolean,
  ) => void;
  isAvailabilitySelected: (itemId: number, availabilityId: string) => boolean;
  shouldShowCheckbox: (availability: CalendarAvailability) => boolean;
  // Bulk delete
  isBulkDeleting: boolean;
  deletePopupOpen: boolean;
  setDeletePopupOpen: (open: boolean) => void;
  successPopupOpen: boolean;
  setSuccessPopupOpen: (open: boolean) => void;
  successfulDeletes: number;
  handleBulkDelete: () => Promise<void>;
  handleDeleteButtonClick: () => void;
  handleSuccessDialogClose: () => void;
}

const AvailabilitiesContext = createContext<AvailabilitiesContextValue | null>(
  null,
);

const DELETED_FAREHARBOR_KEY = "deletedFareharborAvailabilities";

export function AvailabilitiesProvider({
  children,
}: AvailabilitiesProviderProps) {
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [selectedItem, setSelectedItem] = useState<string>("All");
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<SelectedSlot | null>(null);
  const { id } = useParams();

  // Booking stats
  const [bookingStats, setBookingStats] = useState<{
    [date: string]: { [itemId: string]: number };
  }>({});

  // Selection state
  const [selectedAvailabilities, setSelectedAvailabilities] = useState<
    SelectedAvailability[]
  >([]);

  // Bulk delete state
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [deletePopupOpen, setDeletePopupOpen] = useState(false);
  const [successPopupOpen, setSuccessPopupOpen] = useState(false);
  const [successfulDeletes, setSuccessfulDeletes] = useState(0);

  // Deleted FareHarbor tracking
  const [deletedFareharborIds, setDeletedFareharborIds] = useState<Set<string>>(
    () => {
      const stored = localStorage.getItem(DELETED_FAREHARBOR_KEY);
      if (stored) {
        try {
          return new Set(JSON.parse(stored));
        } catch {
          return new Set();
        }
      }
      return new Set();
    },
  );

  // API hooks
  const [getMyBookings] = useLazyGetMyBookingsQuery();
  const [deleteAvailability] = useDeleteAvailabilityMutation();

  const { data: items, isFetching } = useGetBusinessItemsForMonthQuery({
    businessId: id,
    startDate: currentMonth.startOf("month").format("YYYY-MM-DD"),
  });

  const navigateMonth = (direction: "prev" | "next") => {
    const newDate =
      direction === "prev"
        ? currentMonth.subtract(1, "month")
        : currentMonth.add(1, "month");
    setCurrentMonth(newDate);
  };

  const allItems = items?.items || [];

  // Filter items that have availabilities in the current month
  const itemsWithAvailabilities = useMemo(() => {
    return allItems.filter((item) => {
      if (item.record_status === false) return false;

      // Check FareHarbor availabilities
      if (item.item_type === 4 && item.fareharbor_availabilities?.length) {
        return item.fareharbor_availabilities.some((avail) => {
          const fhId = String(avail.pk ?? avail.id);
          if (deletedFareharborIds.has(fhId)) return false;
          if (!avail.start_at) return false;
          const availMonth = dayjs(avail.start_at);
          return availMonth.isSame(currentMonth, "month");
        });
      }

      // Check regular availabilities
      if (item.availabilities?.length) {
        return item.availabilities.some((avail) => {
          if (avail.record_status === false) return false;
          if (!avail.start_time) return false;
          const availMonth = dayjs(avail.start_time);
          return availMonth.isSame(currentMonth, "month");
        });
      }

      return false;
    });
  }, [allItems, currentMonth, deletedFareharborIds]);

  const filteredItems = useMemo(() => {
    if (selectedItem === "All") {
      return itemsWithAvailabilities;
    }
    if (selectedItem === "Direct") {
      // NOTE: Direct means the availability was created to share directly
      // to one user, so they are marked 'private'
      return itemsWithAvailabilities.filter(
        (item) =>
          item.availabilities &&
          item.availabilities.some((avail) => avail.private),
      );
    }
    return itemsWithAvailabilities.filter(
      (item) => getBusinessName(item) === selectedItem,
    );
  }, [itemsWithAvailabilities, selectedItem]);

  const { byDay } = useMemo(
    () => indexAvailabilitiesByDay(filteredItems, deletedFareharborIds),
    [filteredItems, deletedFareharborIds],
  );

  const getAvailabilitiesForDate = useCallback(
    (date: string) =>
      byDay.get(date)?.filter((a) => {
        // filter out non-private availabilities if "Direct" is selected
        if (selectedItem === "Direct") {
          return a.availability.private === true;
        }
        return true;
      }) || [],
    [byDay, selectedItem],
  );

  // Get item by ID and availabilityId
  // have to filter by both since we could have multiple instances of an item returned
  const getItemById = useCallback(
    (itemId: number, availabilityId: number) =>
      allItems.find(
        (item) =>
          item.id === itemId &&
          (item.availabilities?.some((avail) => avail.id === availabilityId) ||
            item.fareharbor_availabilities?.some(
              (avail) => (avail.pk ?? avail.id) === availabilityId,
            )),
      ),
    [allItems],
  );

  // Get item by unique availabilityId
  const getItemByAvailabilityId = useCallback(
    (availabilityId: string, isFareharbor: boolean): Item | undefined => {
      return allItems.find((item) => {
        if (isFareharbor) {
          return item.fareharbor_availabilities?.some(
            (a) => String(a.pk ?? a.id) === availabilityId,
          );
        } else {
          return item.availabilities?.some(
            (a) => String(a.id) === availabilityId,
          );
        }
      });
    },
    [allItems],
  );

  // Helper to select an availability from a CalendarAvailability
  const selectAvailability = useCallback(
    (calendarAvailability: CalendarAvailability) => {
      const item = getItemByAvailabilityId(
        calendarAvailability.availabilityId,
        calendarAvailability.isFareharbor ?? false,
      );
      if (item) {
        setSelectedSlot({
          item,
          availability: calendarAvailability.availability,
          isFareharbor: calendarAvailability.isFareharbor ?? false,
        });
      }
    },
    [getItemByAvailabilityId],
  );

  // Fetch booking stats for current month
  useEffect(() => {
    const fetchBookingStats = async () => {
      if (!id) return;

      const year = currentMonth.year();
      const month = currentMonth.month();
      const daysInMonth = currentMonth.daysInMonth();

      const newStats: { [date: string]: { [itemId: string]: number } } = {};

      // Fetch bookings for each day in the month
      const fetchPromises = [];
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        const dateString = dayjs(date).format("YYYY-MM-DD");

        fetchPromises.push(
          getMyBookings({ business_id: Number(id), date })
            .unwrap()
            .then((response) => {
              const bookingsArray = response?.bookings || response?.data || [];
              const stats: { [itemId: string]: number } = {};

              if (Array.isArray(bookingsArray)) {
                bookingsArray.forEach((booking: any) => {
                  const itemId = booking.referral?.item_id;
                  const ticketsBought = booking.tickets_bought || 1;

                  if (itemId) {
                    const reservationDate = new Date(booking.reservation_time);
                    const reservationDateString =
                      dayjs(reservationDate).format("YYYY-MM-DD");
                    if (reservationDateString === dateString) {
                      stats[itemId] = (stats[itemId] || 0) + ticketsBought;
                    }
                  }
                });
              }

              newStats[dateString] = stats;
            })
            .catch(() => {
              newStats[dateString] = {};
            }),
        );
      }

      await Promise.all(fetchPromises);
      setBookingStats(newStats);
    };

    fetchBookingStats();
  }, [currentMonth, id, getMyBookings]);

  // Get booking count for a specific item on a specific date
  const getBookingCountForItem = useCallback(
    (date: string, itemId: number): number => {
      return bookingStats[date]?.[itemId] || 0;
    },
    [bookingStats],
  );

  // Selection handlers
  const shouldShowCheckbox = useCallback(
    (availability: CalendarAvailability): boolean => {
      const bookingCount = getBookingCountForItem(
        availability.dayKey,
        availability.item.id,
      );
      const hasZeroReservations = bookingCount === 0;
      const isFareharbor = availability.isFareharbor;

      return hasZeroReservations || isFareharbor === true;
    },
    [getBookingCountForItem],
  );

  const isAvailabilitySelected = useCallback(
    (itemId: number, availabilityId: string): boolean => {
      return selectedAvailabilities.some(
        (selected) =>
          selected.itemId === itemId &&
          selected.availabilityId === availabilityId,
      );
    },
    [selectedAvailabilities],
  );

  const handleCheckboxChange = useCallback(
    (availability: CalendarAvailability, isSelected: boolean) => {
      if (isSelected) {
        setSelectedAvailabilities((prev) => [
          ...prev,
          {
            itemId: availability.item.id,
            availabilityId: availability.availabilityId,
            availability,
          },
        ]);
      } else {
        setSelectedAvailabilities((prev) =>
          prev.filter(
            (selected) =>
              !(
                selected.itemId === availability.item.id &&
                selected.availabilityId === availability.availabilityId
              ),
          ),
        );
      }
    },
    [],
  );

  // Bulk delete handlers
  // Calculate selection counts for deletable vs hideable
  const selectionCounts = useMemo(() => {
    const deletableCount = selectedAvailabilities.filter(
      (selected) => !selected.availability.isFareharbor
    ).length;
    const hideableCount = selectedAvailabilities.filter(
      (selected) => selected.availability.isFareharbor
    ).length;
    return { deletableCount, hideableCount };
  }, [selectedAvailabilities]);

  const handleDeleteButtonClick = useCallback(() => {
    if (selectedAvailabilities.length > 0) {
      setDeletePopupOpen(true);
    }
  }, [selectedAvailabilities.length]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedAvailabilities.length === 0) return;

    setIsBulkDeleting(true);
    setDeletePopupOpen(false);

    try {
      const deletePromises = selectedAvailabilities.map(async (selected) => {
        try {
          const isFareharbor = selected.availability.isFareharbor;

          if (!isFareharbor) {
            // Delete regular availability via API
            const availabilityId = Number(selected.availabilityId);
            if (availabilityId) {
              await deleteAvailability(availabilityId).unwrap();
              return { success: true, type: "api" as const };
            }
          } else {
            // FareHarbor - just track in localStorage
            return {
              success: true,
              type: "fareharbor" as const,
              id: selected.availabilityId,
            };
          }
          return { success: false };
        } catch (error) {
          console.error(
            `Error deleting availability ${selected.availabilityId}:`,
            error,
          );
          return { success: false };
        }
      });

      const results = await Promise.allSettled(deletePromises);
      const successfulResults = results.filter(
        (result) => result.status === "fulfilled" && result.value?.success,
      );

      setSuccessfulDeletes(successfulResults.length);
      setSuccessPopupOpen(true);

      // Collect deleted FareHarbor IDs
      const newDeletedFareharborIds = new Set(deletedFareharborIds);
      successfulResults.forEach((result) => {
        if (
          result.status === "fulfilled" &&
          result.value.type === "fareharbor" &&
          result.value.id
        ) {
          newDeletedFareharborIds.add(result.value.id);
        }
      });

      // Update state and localStorage
      setDeletedFareharborIds(newDeletedFareharborIds);
      localStorage.setItem(
        DELETED_FAREHARBOR_KEY,
        JSON.stringify(Array.from(newDeletedFareharborIds)),
      );

      // Clear selection
      setSelectedAvailabilities([]);
    } catch (error) {
      console.error("Bulk delete error:", error);
    } finally {
      setIsBulkDeleting(false);
    }
  }, [selectedAvailabilities, deleteAvailability, deletedFareharborIds]);

  const handleSuccessDialogClose = useCallback(() => {
    setSuccessPopupOpen(false);
  }, []);

  return (
    <AvailabilitiesContext.Provider
      value={{
        currentMonth,
        setCurrentMonth,
        navigateMonth,
        items: allItems,
        filteredItems,
        selectedItem,
        setSelectedItem,
        isLoading: isFetching,
        selectedDate,
        setSelectedDate,
        getAvailabilitiesForDate,
        selectedSlot,
        setSelectedSlot,
        selectAvailability,
        getItemById,
        // Booking stats
        getBookingCountForItem,
        // Selection
        selectedAvailabilities,
        selectionCounts,
        handleCheckboxChange,
        isAvailabilitySelected,
        shouldShowCheckbox,
        // Bulk delete
        isBulkDeleting,
        deletePopupOpen,
        setDeletePopupOpen,
        successPopupOpen,
        setSuccessPopupOpen,
        successfulDeletes,
        handleBulkDelete,
        handleDeleteButtonClick,
        handleSuccessDialogClose,
      }}
    >
      {children}
    </AvailabilitiesContext.Provider>
  );
}

export function useAvailabilities() {
  const context = useContext(AvailabilitiesContext);
  if (!context) {
    throw new Error(
      "useAvailabilities must be used within an AvailabilitiesProvider",
    );
  }
  return context;
}
